/**
 * Filter utilities for the new BFF API spec
 * Implements the filter=campo:operador:valor pattern
 */

/**
 * Filter operators supported by the API
 */
export type FilterOperator
  = | 'eq' // Equal to
    | 'ne' // Not equal to
    | 'gt' // Greater than
    | 'lt' // Less than
    | 'ge' // Greater than or equal (>=)
    | 'le' // Less than or equal (<=)
    | 'in' // Contained in list
    | 'not_in' // Not contained in list
    | 'like' // SQL LIKE pattern
    | 'ilike' // Case-insensitive LIKE
    | 'is_null' // Is null
    | 'is_not_null' // Is not null

/**
 * Parsed filter structure
 */
export interface ParsedFilter {
  field: string
  operator: FilterOperator
  value: string | string[]
}

/**
 * Filter option for UI display
 */
export interface FilterOption {
  value: string
  label: string
  count: number
}

/**
 * Available filter definition
 */
export interface AvailableFilter {
  field: string
  label: string
  type: 'select' | 'multiselect' | 'date' | 'daterange' | 'number' | 'text'
  options: FilterOption[]
}

/**
 * Filter metadata for API responses
 */
export interface FilterMetadata {
  available: AvailableFilter[]
  applied: ParsedFilter[]
}

/**
 * Parse filter parameter from URL
 * Format: campo:operador:valor
 *
 * @example
 * parseFilter('status:eq:paid') // { field: 'status', operator: 'eq', value: 'paid' }
 * parseFilter('amount:ge:100') // { field: 'amount', operator: 'ge', value: '100' }
 */
export function parseFilter(filterString: string): ParsedFilter | null {
  const parts = filterString.split(':')

  if (parts.length < 2) {
    return null
  }

  const field = parts[0]
  const operator = parts[1] as FilterOperator

  if (!field) {
    return null
  }

  // For is_null and is_not_null, value is optional
  if (operator === 'is_null' || operator === 'is_not_null') {
    return { field, operator, value: '' }
  }

  if (parts.length < 3) {
    return null
  }

  // Join remaining parts in case value contains ':'
  const value = parts.slice(2).join(':')

  // Parse 'in' and 'not_in' as arrays
  if (operator === 'in' || operator === 'not_in') {
    return {
      field,
      operator,
      value: value.split(',').map(v => v.trim())
    }
  }

  return { field, operator, value }
}

/**
 * Parse all filter parameters from URL search params
 * Supports semicolon-separated filters: filter=field1:op:val1;field2:op:val2
 */
export function parseFilters(searchParams: URLSearchParams): ParsedFilter[] {
  const filters: ParsedFilter[] = []
  const filterParam = searchParams.get('filter')

  if (!filterParam) {
    return filters
  }

  // Split by semicolon to support multiple filters in one param
  const filterStrings = filterParam.split(';')

  for (const filterString of filterStrings) {
    const parsed = parseFilter(filterString.trim())
    if (parsed) {
      filters.push(parsed)
    }
  }

  return filters
}

/**
 * Apply a single filter to a value
 */
function applyFilterOperator(
  value: unknown,
  operator: FilterOperator,
  filterValue: string | string[]
): boolean {
  // Handle null checks
  if (operator === 'is_null') {
    return value === null || value === undefined
  }

  if (operator === 'is_not_null') {
    return value !== null && value !== undefined
  }

  // If value is null/undefined and operator isn't null check, return false
  if (value === null || value === undefined) {
    return false
  }

  // Convert value to string for comparison
  const strValue = String(value).toLowerCase()

  switch (operator) {
    case 'eq':
      return String(value) === String(filterValue)

    case 'ne':
      return String(value) !== String(filterValue)

    case 'gt':
      if (typeof value === 'number' || !isNaN(Number(value))) {
        return Number(value) > Number(filterValue)
      }
      return strValue > String(filterValue).toLowerCase()

    case 'lt':
      if (typeof value === 'number' || !isNaN(Number(value))) {
        return Number(value) < Number(filterValue)
      }
      return strValue < String(filterValue).toLowerCase()

    case 'ge':
      if (typeof value === 'number' || !isNaN(Number(value))) {
        return Number(value) >= Number(filterValue)
      }
      return strValue >= String(filterValue).toLowerCase()

    case 'le':
      if (typeof value === 'number' || !isNaN(Number(value))) {
        return Number(value) <= Number(filterValue)
      }
      return strValue <= String(filterValue).toLowerCase()

    case 'in':
      if (!Array.isArray(filterValue)) return false
      return filterValue.some(v => String(value) === String(v))

    case 'not_in':
      if (!Array.isArray(filterValue)) return false
      return !filterValue.some(v => String(value) === String(v))

    case 'like': {
      if (typeof filterValue !== 'string') return false
      const likePattern = filterValue.replace(/%/g, '.*').replace(/_/g, '.')
      return new RegExp(`^${likePattern}$`).test(String(value))
    }

    case 'ilike': {
      if (typeof filterValue !== 'string') return false
      const ilikePattern = filterValue.replace(/%/g, '.*').replace(/_/g, '.')
      return new RegExp(`^${ilikePattern}$`, 'i').test(String(value))
    }

    default:
      return false
  }
}

/**
 * Get nested property value from object using dot notation
 */
export function getNestedValue<T extends object>(obj: T, path: string): unknown {
  return path.split('.').reduce<unknown>((current, prop) => {
    if (current && typeof current === 'object' && prop in current) {
      return (current as Record<string, unknown>)[prop]
    }
    return undefined
  }, obj)
}

/**
 * Apply filters to an array of data
 */
export function applyFilters<T extends object>(
  data: T[],
  filters: ParsedFilter[]
): T[] {
  if (filters.length === 0) {
    return data
  }

  return data.filter((item) => {
    // Item must match ALL filters (AND logic)
    return filters.every((filter) => {
      const value = getNestedValue(item, filter.field)
      return applyFilterOperator(value, filter.operator, filter.value)
    })
  })
}

/**
 * Parse and apply sorting from URL
 * Format: campo:direction (e.g., 'created:desc', 'amount:asc')
 */
export function applySorting<T extends object>(
  data: T[],
  sortParam: string | null,
  defaultSort = 'created:desc'
): T[] {
  const sortString = sortParam || defaultSort
  const [field, direction = 'desc'] = sortString.split(':')

  if (!field) {
    return data
  }

  const sortedData = [...data]

  sortedData.sort((a, b) => {
    const aValue = getNestedValue(a, field)
    const bValue = getNestedValue(b, field)

    // Handle null/undefined
    if (aValue === null || aValue === undefined) return 1
    if (bValue === null || bValue === undefined) return -1

    // Numeric comparison
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return direction === 'asc' ? aValue - bValue : bValue - aValue
    }

    // String comparison
    const aStr = String(aValue).toLowerCase()
    const bStr = String(bValue).toLowerCase()

    if (direction === 'asc') {
      return aStr.localeCompare(bStr)
    } else {
      return bStr.localeCompare(aStr)
    }
  })

  return sortedData
}

/**
 * Apply pagination to data
 */
export function applyPagination<T extends object>(
  data: T[],
  page: number,
  limit: number
): {
  data: T[]
  page: number
  limit: number
  total: number
  pages: number
} {
  const total = data.length
  const pages = Math.ceil(total / limit)
  const start = (page - 1) * limit
  const end = start + limit

  return {
    data: data.slice(start, end),
    page,
    limit,
    total,
    pages
  }
}

/**
 * Process a full list request with filters, sorting, and pagination
 */
export function processListRequest<T extends object>(
  data: T[],
  searchParams: URLSearchParams,
  options: {
    defaultSort?: string
    defaultLimit?: number
  } = {}
): {
  page: number
  limit: number
  total: number
  pages: number
  data: T[]
} {
  const { defaultSort = 'created:desc', defaultLimit = 20 } = options

  // 1. Parse parameters
  const page = Math.max(1, Number.parseInt(searchParams.get('page') || '1', 10))
  const limit = Math.max(1, Math.min(100, Number.parseInt(searchParams.get('limit') || String(defaultLimit), 10)))
  const sort = searchParams.get('sort')
  const filters = parseFilters(searchParams)

  // 2. Apply filters
  let processedData = applyFilters(data, filters)

  // 3. Apply sorting
  processedData = applySorting(processedData, sort, defaultSort)

  // 4. Apply pagination
  return applyPagination(processedData, page, limit)
}
